import {trim} from 'lodash'
require('./foo-tv-shaders')

let camera, scene, renderer
let video, videoTexture, videoMaterial, videoSrc
let image, imageTexture, imageMaterial, imageSrc
let planeGeometry, plane
let composer
let shaderTime = 0
let badTVParams, badTVPass
let staticParams, staticPass
let rgbParams, rgbPass
let filmParams, filmPass
let renderPass, copyPass
let pnoise, globalParams

const KEYCODE_SPACE = 32

export default (selector) => {

  const $container = $(selector)

  if ($container) {
    videoSrc = $container.attr('data-video-src')
    imageSrc = $container.attr('data-image-src')
  }

  if (!videoSrc && !imageSrc) {
    console.warn('Did not receive an image or video src (foo-tv.js)')
    return
  }

  init({videoSrc, imageSrc}, $container)
  animate()
}

function init(srcSet, $container) {
  const {videoSrc: videoSrc, imageSrc: imageSrc} = srcSet

  scene = new THREE.Scene()
  camera = new THREE.PerspectiveCamera(55, 1080 / 720, 20, 3000)
  camera.position.z = 1000

  // Load Video
  if (videoSrc && trim(videoSrc).length) {
    video = document.createElement('video')
    video.loop = true
    video.src = videoSrc
    video.volume = 0
    video.setAttribute('crossorigin', 'anonymous')
    video.load()
    video.play()

    videoTexture = new THREE.Texture(video)
    videoTexture.minFilter = THREE.LinearFilter
    videoTexture.magFilter = THREE.LinearFilter

    videoMaterial = new THREE.MeshBasicMaterial({
      map: videoTexture
    })

    // Add video plane
    planeGeometry = new THREE.PlaneGeometry(1080, 720, 1, 1)
    plane = new THREE.Mesh(planeGeometry, videoMaterial)
  } else if (imageSrc && trim(imageSrc).length) {
    image = document.createElement('img')
    image.setAttribute('crossorigin', 'anonymous')
    image.width = '100%'
    image.height = '100%'
    image.src = imageSrc

    imageTexture = new THREE.Texture(image)
    imageTexture.minFilter = THREE.LinearFilter
    imageTexture.magFilter = THREE.LinearFilter

    imageMaterial = new THREE.MeshBasicMaterial({
      map: imageTexture
    })

    // Add image plane
    planeGeometry = new THREE.PlaneGeometry(1080, 720, 1, 1)
    plane = new THREE.Mesh(planeGeometry, imageMaterial)
  }

  scene.add(plane)
  plane.z = 0
  plane.scale.x = 1.45
  plane.scale.y = 1.45

  // Init renderer
  renderer = new THREE.WebGLRenderer()
  renderer.setSize(800, 600)
  $container.append(renderer.domElement)

  // Post processing
  renderPass = new THREE.RenderPass(scene, camera)
  badTVPass = new THREE.ShaderPass(THREE.BadTVShader)
  rgbPass = new THREE.ShaderPass(THREE.RGBShiftShader)
  filmPass = new THREE.ShaderPass(THREE.FilmShader)
  staticPass = new THREE.ShaderPass(THREE.StaticShader)
  copyPass = new THREE.ShaderPass(THREE.CopyShader)

  // Set shader uniforms
  filmPass.uniforms.grayscale.value = 0

  badTVParams = {
    mute: true,
    show: true,
    distortion: 1.2,
    distortion2: 2.7,
    speed: 0.06,
    rollSpeed: 0.005,
  }

  staticParams = {
    show: false,
    amount: 0.5,
    // size: 4.0,
  }

  rgbParams = {
    show: true,
    amount: 0.010,
    angle: 2.99,
  }

  filmParams = {
    show: true,
    count: 1000,
    sIntensity: 0.64,
    nIntensity: 0.38,
  }

  onToggleShaders()
  onParamsChange()

  window.addEventListener('resize', onResize, false)
  window.addEventListener('keydown', (event) => {
    if (event.which === KEYCODE_SPACE && event.target === document.body) {
      event.preventDefault()
      randomizeParams()
      return false
    }
  }, false)

  onResize()
}

// Copy params into shader uniforms
function onParamsChange() {
  badTVPass.uniforms['distortion'].value = badTVParams.distortion
  badTVPass.uniforms['distortion2'].value = badTVParams.distortion2
  badTVPass.uniforms['speed'].value = badTVParams.speed
  badTVPass.uniforms['rollSpeed'].value = badTVParams.rollSpeed

  staticPass.uniforms['amount'].value = staticParams.amount
  staticPass.uniforms['size'].value = staticParams.size

  rgbPass.uniforms['angle'].value = rgbParams.angle * Math.PI
  rgbPass.uniforms['amount'].value = rgbParams.amount

  filmPass.uniforms['sCount'].value = filmParams.count
  filmPass.uniforms['sIntensity'].value = filmParams.sIntensity
  filmPass.uniforms['nIntensity'].value = filmParams.nIntensity
}

function randomizeParams() {
  if (Math.random() < 0.2) {
    badTVParams.distortion = 0.1
    badTVParams.distortion2 = 0.1
    badTVParams.speed = 0
    badTVParams.rollSpeed = 0
    rgbParams.angle = 0
    rgbParams.amount = 0
    staticParams.amount = 0
  } else {
    badTVParams.distortion = Math.random() * 10 + 0.1
    badTVParams.distortion2 = Math.random() * 10 + 0.1
    badTVParams.speed = Math.random() * 0.4
    badTVParams.rollSpeed = Math.random() * 0.15
    rgbParams.angle = Math.random() * 2
    rgbParams.amount = Math.random() * 0.03
    staticParams.amount = Math.random() * 0.1
  }

  onParamsChange()
}

function onToggleShaders() {
  // Add shader passes to composer (order is important)
  composer = new THREE.EffectComposer(renderer)
  composer.addPass(renderPass)

  if (filmParams.show) {
    composer.addPass(filmPass)
  }

  if (badTVParams.show) {
    composer.addPass(badTVPass)
  }

  if (rgbParams.show) {
    composer.addPass(rgbPass)
  }

  if (staticParams.show) {
    composer.addPass(staticPass)
  }

  composer.addPass(copyPass)
  copyPass.renderToScreen = true
}

function animate() {
  shaderTime += 0.05
  badTVPass.uniforms['time'].value = shaderTime
  filmPass.uniforms['time'].value = shaderTime
  staticPass.uniforms['time'].value = shaderTime

  if (videoReady(video) && videoTexture) {
    videoTexture.needsUpdate = true
  }

  if (imageReady(image) && imageTexture) {
    imageTexture.needsUpdate = true
  }

  window.requestAnimationFrame(animate)
  composer.render(0.1)
}

function onResize() {
  const aspectRatio = window.innerWidth / window.innerHeight

  renderer.setSize(window.innerWidth, window.innerHeight)
  camera.aspect = aspectRatio

  if (window.innerWidth > 768) {
    camera.zoom = aspectRatio * 0.689655172
  }

  camera.updateProjectionMatrix()
}

function imageReady(imageEl) {
  return (typeof imageEl !== 'undefined') && imageEl.complete && (imageEl.naturalHeight !== 0)
}

function videoReady(videoEl) {
  return (typeof videoEl !== 'undefined') && (videoEl.readyState === videoEl.HAVE_ENOUGH_DATA)
}


// WEBPACK FOOTER //
// ./foo-tv.js